import React, { useEffect, useRef, useState } from "react";
import { ArrowRight } from "lucide-react";
import { getSmartCTA } from "../../lib/conversionState";

/**
 * StickyMobileCTA — a single, contextual, mobile-only call-to-action bar
 * that sits above the safe-area bottom.
 *
 * Hero behavior: instead of hiding, we surface the visitor's "smart CTA"
 * — the most relevant next step given their localStorage history
 * (snapshot completed, book preview viewed, blueprint started, etc.).
 * Every other section keeps its hardcoded contextual CTA so the visitor
 * always has a present-tense next step that matches the section they're
 * reading.
 *
 * Hidden during modals (via the `am-modal-open` class on <html>),
 * during the Consultation section (modal CTA already prominent), and
 * during the Final CTA section (would duplicate).
 */
const STATIC_CTA_BY_SECTION = {
  "what-it-helps": { label: "Take the Free Clarity Snapshot", href: "/frequency-snapshot" },
  pathway: { label: "Take the Free Clarity Snapshot", href: "/frequency-snapshot" },
  "orientation-reset-bridge": {
    label: "Book the Clarity Reset Session · $149",
    target: null,
    dispatch: "am:open-orientation-reset-checkout",
  },
  "showcase-teaser": { label: "Take the Free Clarity Snapshot", href: "/frequency-snapshot" },
  wisdom: { label: "Take the Free Clarity Snapshot", href: "/frequency-snapshot" },
  "journal-teaser": { label: "Take the Free Clarity Snapshot", href: "/frequency-snapshot" },
  "final-cta": null,
};

const StickyMobileCTA = () => {
  const [currentSection, setCurrentSection] = useState("hero");
  const [hidden, setHidden] = useState(false);
  const sectionsRef = useRef([]);

  useEffect(() => {
    if (window.matchMedia("(min-width: 1024px)").matches) return undefined;
    const sections = Array.from(document.querySelectorAll("section[id]"));
    if (sections.length === 0) return undefined;
    sectionsRef.current = sections;
    const visibility = new Map();
    const obs = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          visibility.set(entry.target.id, entry.intersectionRatio);
        }
        let bestId = "hero";
        let bestRatio = 0;
        for (const [id, ratio] of visibility) {
          if (ratio > bestRatio) {
            bestRatio = ratio;
            bestId = id;
          }
        }
        setCurrentSection(bestId);
      },
      { threshold: [0.15, 0.3, 0.5, 0.7] }
    );
    sections.forEach((s) => obs.observe(s));
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    const update = () => {
      const open = document.documentElement.classList.contains("am-modal-open");
      setHidden(open);
    };
    update();
    const obs = new MutationObserver(update);
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    return () => obs.disconnect();
  }, []);

  // On Hero, use the visitor's smart CTA. Everywhere else, the static
  // section CTA. We resolve this on every render so a fresh
  // localStorage value lands immediately after the visitor completes
  // the snapshot or purchases the book.
  let cta = STATIC_CTA_BY_SECTION[currentSection];
  if (currentSection === "hero") {
    const smart = getSmartCTA();
    cta = { label: smart.label, href: smart.href };
  }
  if (!cta || hidden) return null;

  const handleClick = () => {
    if (cta.href) {
      if (cta.href.startsWith("/#")) {
        const id = cta.href.slice(2);
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
          return;
        }
      }
      window.location.href = cta.href;
      return;
    }
    if (cta.target) {
      const el = document.querySelector(`[data-testid="${cta.target}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        setTimeout(() => el.click(), 380);
        return;
      }
    }
    if (cta.dispatch) {
      window.dispatchEvent(new CustomEvent(cta.dispatch));
      return;
    }
    if (cta.scrollTo) {
      const el = document.getElementById(cta.scrollTo);
      el?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <div className="sticky-mobile-cta-root" data-testid="sticky-mobile-cta">
      <button
        type="button"
        onClick={handleClick}
        className="sticky-mobile-cta-btn"
        data-testid="sticky-mobile-cta-button"
      >
        <span>{cta.label}</span>
        <ArrowRight size={14} strokeWidth={1.6} />
      </button>
    </div>
  );
};

export default StickyMobileCTA;
